import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, Loader2, X } from 'lucide-react';
import usCities from "../../data/us-cities.json";

const US_STATES = [
  { name: "Alabama", abbr: "AL" },
  { name: "Alaska", abbr: "AK" },
  { name: "Arizona", abbr: "AZ" },
  { name: "Arkansas", abbr: "AR" },
  { name: "California", abbr: "CA" },
  { name: "Colorado", abbr: "CO" },
  { name: "Connecticut", abbr: "CT" },
  { name: "Delaware", abbr: "DE" },
  { name: "Florida", abbr: "FL" },
  { name: "Georgia", abbr: "GA" },
  { name: "Hawaii", abbr: "HI" },
  { name: "Idaho", abbr: "ID" },
  { name: "Illinois", abbr: "IL" },
  { name: "Indiana", abbr: "IN" },
  { name: "Iowa", abbr: "IA" },
  { name: "Kansas", abbr: "KS" },
  { name: "Kentucky", abbr: "KY" },
  { name: "Louisiana", abbr: "LA" },
  { name: "Maine", abbr: "ME" },
  { name: "Maryland", abbr: "MD" },
  { name: "Massachusetts", abbr: "MA" },
  { name: "Michigan", abbr: "MI" },
  { name: "Minnesota", abbr: "MN" },
  { name: "Mississippi", abbr: "MS" },
  { name: "Missouri", abbr: "MO" },
  { name: "Montana", abbr: "MT" },
  { name: "Nebraska", abbr: "NE" },
  { name: "Nevada", abbr: "NV" },
  { name: "New Hampshire", abbr: "NH" },
  { name: "New Jersey", abbr: "NJ" },
  { name: "New Mexico", abbr: "NM" },
  { name: "New York", abbr: "NY" },
  { name: "North Carolina", abbr: "NC" },
  { name: "North Dakota", abbr: "ND" },
  { name: "Ohio", abbr: "OH" },
  { name: "Oklahoma", abbr: "OK" },
  { name: "Oregon", abbr: "OR" },
  { name: "Pennsylvania", abbr: "PA" },
  { name: "Rhode Island", abbr: "RI" },
  { name: "South Carolina", abbr: "SC" },
  { name: "South Dakota", abbr: "SD" },
  { name: "Tennessee", abbr: "TN" },
  { name: "Texas", abbr: "TX" },
  { name: "Utah", abbr: "UT" },
  { name: "Vermont", abbr: "VT" },
  { name: "Virginia", abbr: "VA" },
  { name: "Washington", abbr: "WA" },
  { name: "West Virginia", abbr: "WV" },
  { name: "Wisconsin", abbr: "WI" },
  { name: "Wyoming", abbr: "WY" },
];

const getLocalSuggestions = (query) => {
  if (!query || query.length < 1) return [];
  
  const lowerQuery = query.toLowerCase();
  const results = [];
  
  usCities.forEach(c => {
    const cityLower = c.city.toLowerCase();
    const stateLower = c.state.toLowerCase();
    const display = `${c.city}, ${c.state}`;
    
    if (cityLower.includes(lowerQuery) || stateLower.includes(lowerQuery)) {
      results.push({
        id: `city-${c.city}-${c.state}`,
        name: c.city,
        address: display,
        city: c.city,
        state: c.state,
        type: 'city',
        isLocal: true,
        startsWith: cityLower.startsWith(lowerQuery)
      });
    }
  });
  
  US_STATES.forEach(s => {
    const nameLower = s.name.toLowerCase();
    const abbrLower = s.abbr.toLowerCase();
    
    if (nameLower.includes(lowerQuery) || abbrLower.includes(lowerQuery)) {
      results.push({
        id: `state-${s.abbr}`,
        name: s.name,
        address: s.name,
        state: s.abbr,
        type: 'state',
        isLocal: true,
        startsWith: nameLower.startsWith(lowerQuery)
      });
    }
  });
  
  return results.sort((a, b) => {
    if (a.startsWith && !b.startsWith) return -1;
    if (!a.startsWith && b.startsWith) return 1;
    if (a.type === 'city' && b.type === 'state') return -1;
    if (a.type === 'state' && b.type === 'city') return 1;
    return a.name.localeCompare(b.name);
  }).slice(0, 8);
};

const LocationAutocomplete = ({
  value = '',
  onChange,
  onSelect,
  placeholder = 'Search for a location...',
  label,
  required = false,
  className = '',
  error = '',
  mode = 'full',
  compact = false,
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  const apiKey = process.env.REACT_APP_GEOAPIFY_API_KEY;

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const fetchApiSuggestions = useCallback(async (query) => {
    if (!query || query.length < 3 || !apiKey) {
      return [];
    }

    try {
      const response = await fetch(
        `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(query)}&limit=5&apiKey=${apiKey}`
      );
      const data = await response.json();
      
      if (data.features) {
        return data.features.map((feature) => ({
          id: feature.properties.place_id,
          name: feature.properties.name || feature.properties.street,
          address: feature.properties.formatted,
          city: feature.properties.city,
          state: feature.properties.state,
          country: feature.properties.country,
          postcode: feature.properties.postcode,
          lat: feature.properties.lat,
          lon: feature.properties.lon,
          type: feature.properties.result_type,
          isLocal: false,
        }));
      }
      return [];
    } catch (error) {
      console.error('Error fetching location suggestions:', error);
      return [];
    }
  }, [apiKey]);

  const fetchSuggestions = useCallback(async (query) => {
    if (!query) {
      setSuggestions([]);
      return;
    }

    const localResults = getLocalSuggestions(query);
    
    if (mode === 'local') {
      setSuggestions(localResults);
      setShowDropdown(localResults.length > 0);
      return;
    }

    setSuggestions(localResults);
    setShowDropdown(localResults.length > 0);

    if (query.length >= 3 && apiKey) {
      setIsLoading(true);
      try {
        const apiResults = await fetchApiSuggestions(query);
        
        const combinedResults = [...localResults];
        apiResults.forEach(apiResult => {
          const exists = combinedResults.some(
            local => local.address?.toLowerCase() === apiResult.address?.toLowerCase()
          );
          if (!exists) {
            combinedResults.push(apiResult);
          }
        });
        
        setSuggestions(combinedResults.slice(0, 10));
        setShowDropdown(combinedResults.length > 0);
      } catch (error) {
        console.error('API fetch failed, using local results:', error);
      } finally {
        setIsLoading(false);
      }
    }
  }, [apiKey, mode, fetchApiSuggestions]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setSelectedIndex(-1);
    
    if (onChange) {
      onChange(newValue);
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchSuggestions(newValue);
    }, mode === 'local' ? 50 : 300);
  };

  const handleSelect = (suggestion) => {
    const displayValue = suggestion.type === 'state' ? suggestion.name : suggestion.address;
    setInputValue(displayValue);
    setSuggestions([]);
    setShowDropdown(false);
    
    if (onSelect) {
      onSelect(suggestion);
    }
    if (onChange) {
      onChange(displayValue);
    }
  };

  const handleKeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && suggestions[selectedIndex]) {
          handleSelect(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleClear = () => {
    setInputValue('');
    setSuggestions([]);
    setShowDropdown(false);
    if (onChange) {
      onChange('');
    }
    if (onSelect) {
      onSelect(null);
    }
    inputRef.current?.focus();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !inputRef.current?.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const highlightMatch = (text, query) => {
    if (!query || !text) return text;
    
    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const index = lowerText.indexOf(lowerQuery);
    
    if (index === -1) return text;
    
    return (
      <>
        {text.slice(0, index)}
        <span className="font-semibold text-primary-600">
          {text.slice(index, index + query.length)}
        </span>
        {text.slice(index + query.length)}
      </>
    );
  };

  return (
    <div className={`relative ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
          <MapPin className={`${compact ? 'h-4 w-4' : 'h-5 w-5'} text-gray-400`} />
        </div>
        
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (inputValue) {
              fetchSuggestions(inputValue);
            }
          }}
          placeholder={placeholder}
          autoComplete="off"
          className={`w-full ${compact ? 'pl-9 pr-8 py-2 text-sm' : 'pl-10 pr-10 py-2.5'} border rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all bg-white ${
            error ? 'border-red-300 bg-red-50' : 'border-grey-200'
          }`}
        />
        
        <div className={`absolute ${compact ? 'right-2' : 'right-3'} top-1/2 -translate-y-1/2 flex items-center gap-1`}>
          {isLoading && (
            <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
          )}
          {inputValue && !isLoading && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className={`${compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} text-gray-400`} />
            </button>
          )}
        </div>
      </div>

      {error && (
        <p className="mt-1 text-sm text-red-500">{error}</p>
      )}

      {showDropdown && suggestions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-grey-200 rounded-lg shadow-lg max-h-64 overflow-auto"
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion.id}
              type="button"
              onClick={() => handleSelect(suggestion)}
              className={`w-full px-3 ${compact ? 'py-2' : 'py-2.5'} flex items-center gap-2 text-left transition-colors ${
                index === selectedIndex 
                  ? 'bg-primary-50 text-primary-700' 
                  : 'text-grey-700 hover:bg-grey-50'
              } ${index !== suggestions.length - 1 ? 'border-b border-grey-100' : ''}`}
            >
              <MapPin className={`${compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} flex-shrink-0 ${
                index === selectedIndex ? 'text-primary-500' : 'text-grey-400'
              }`} />
              <div className="flex-1 min-w-0">
                <span className={`${compact ? 'text-sm' : 'text-sm'}`}>
                  {highlightMatch(
                    suggestion.type === 'state' ? suggestion.name : suggestion.address,
                    inputValue
                  )}
                </span>
              </div>
              <span className={`text-xs px-1.5 py-0.5 rounded flex-shrink-0 ${
                suggestion.type === 'state' 
                  ? 'bg-blue-50 text-blue-600' 
                  : suggestion.isLocal 
                    ? 'bg-grey-100 text-grey-500'
                    : 'bg-violet-50 text-violet-600'
              }`}>
                {suggestion.type === 'state' ? 'State' : suggestion.isLocal ? 'City' : 'Address'}
              </span>
            </button>
          ))}
        </div>
      )}

      {showDropdown && inputValue.length >= 2 && suggestions.length === 0 && !isLoading && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-grey-200 rounded-lg shadow-lg p-3 text-center">
          <p className="text-sm text-grey-500">No locations found</p>
        </div>
      )}
    </div>
  );
};

export default LocationAutocomplete;
